<template>
    <a-modal
        :visible="visible"
        :width="960"
        :maskClosable="false"
        title="选择装修图片"
        class="mall-v2-image-picker"
        @cancel="close"
    >
        <div class="picker-toolbar">
            <a-select :value="selectedDir" class="dir-select" placeholder="全部图片" @change="handleDirChange">
                <a-select-option value="">全部图片</a-select-option>
                <a-select-option v-for="item in dirList" :key="item.id" :value="item.id">{{ item.dirName }}</a-select-option>
            </a-select>
            <a-input-search v-model="newDirName" class="dir-create" placeholder="新建图片分组" enter-button="新建" @search="addDir" />
            <a-upload
                :action="uploadUrl"
                :headers="headers"
                :showUploadList="false"
                accept=".jpg,.jpeg,.png"
                name="file"
                @change="handleUploadChange"
            >
                <a-button type="primary" icon="cloud-upload">上传图片</a-button>
            </a-upload>
        </div>

        <a-spin :spinning="loading">
            <div v-if="imageList.length" class="image-grid">
                <div
                    v-for="item in imageList"
                    :key="item.id"
                    class="image-card"
                    :class="{ active: isSelected(item) }"
                    @click="toggleImage(item)"
                >
                    <img :src="image(item.path)" :alt="item.imageName" />
                    <div class="image-meta">
                        <span>{{ item.imageName || '未命名图片' }}</span>
                        <a-tag v-if="isSelected(item)" color="red">{{ selectedIndex(item) }}</a-tag>
                    </div>
                </div>
            </div>
            <a-empty v-else description="暂无图片，上传后即可选择" />
        </a-spin>

        <div slot="footer" class="picker-footer">
            <pagination :ipagination="ipagination" @change="changePage" @showSizeChange="showSizeChange" />
            <div class="footer-actions">
                <span class="selected-count">已选 {{ selectedImages.length }} 张</span>
                <a-button @click="close">取消</a-button>
                <a-button type="primary" @click="confirm">确定</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import Vue from 'vue'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import Pagination from '@/components/tools/Pagination'
import { image } from '@/utils/Tool'
import {
    addMallDecorateImageDir,
    getMallDecorateImageUploadUrl,
    listMallDecorateImage,
    listMallDecorateImageDir
} from '@/api/mallDecorateV2'

export default {
    name: 'MallV2ImagePicker',
    components: { Pagination },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: false
        },
        value: {
            type: [String, Array],
            default: ''
        }
    },
    data() {
        return {
            loading: false,
            headers: {},
            selectedDir: '',
            newDirName: '',
            dirList: [],
            imageList: [],
            selectedImages: [],
            ipagination: {
                current: 1,
                pageSize: 24,
                pageSizeOptions: ['24', '48', '96'],
                showTotal: (total, range) => `${range[0]}-${range[1]} 共${total}张`,
                showQuickJumper: true,
                showSizeChanger: true,
                total: 0
            }
        }
    },
    computed: {
        uploadUrl() {
            return getMallDecorateImageUploadUrl(this.selectedDir)
        }
    },
    watch: {
        visible(value) {
            if (value) {
                this.open()
            }
        }
    },
    methods: {
        image,
        open() {
            const token = Vue.ls.get(ACCESS_TOKEN)
            this.headers = token ? { 'X-Access-Token': token } : {}
            this.syncSelectedFromValue()
            this.loadDir()
            this.loadImage(1)
        },
        syncSelectedFromValue() {
            const values = Array.isArray(this.value) ? this.value : (this.value ? [this.value] : [])
            this.selectedImages = values.map(path => ({ id: path, path, imageName: path }))
        },
        loadDir() {
            listMallDecorateImageDir().then(res => {
                if (res.success && res.result) {
                    this.dirList = res.result.records || []
                }
            })
        },
        loadImage(pageNo) {
            this.loading = true
            listMallDecorateImage({
                imageDir: this.selectedDir || undefined,
                pageSize: this.ipagination.pageSize,
                pageNo
            }).then(res => {
                if (res.success && res.result) {
                    this.imageList = res.result.records || []
                    this.ipagination.current = res.result.current || pageNo
                    this.ipagination.total = res.result.total || 0
                }
            }).finally(() => {
                this.loading = false
            })
        },
        handleDirChange(value) {
            this.selectedDir = value
            this.loadImage(1)
        },
        addDir() {
            const dirName = (this.newDirName || '').trim()
            if (!dirName) {
                this.$message.warning('请输入分组名称')
                return
            }
            this.loading = true
            addMallDecorateImageDir(dirName).then(res => {
                if (res.success) {
                    this.$message.success('图片分组已创建')
                    this.newDirName = ''
                    this.loadDir()
                    if (res.result && res.result.id) {
                        this.selectedDir = res.result.id
                        this.loadImage(1)
                    }
                }
            }).finally(() => {
                this.loading = false
            })
        },
        handleUploadChange({ file }) {
            if (file.status === 'uploading') {
                this.loading = true
                return
            }
            if (file.status === 'done') {
                this.loading = false
                if (file.response && file.response.success) {
                    this.$message.success('图片已上传')
                    this.loadImage(1)
                } else {
                    this.$message.error(file.response && file.response.message ? file.response.message : '图片上传失败')
                }
            } else if (file.status === 'error') {
                this.loading = false
                this.$message.error('图片上传失败')
            }
        },
        toggleImage(item) {
            const index = this.selectedImages.findIndex(selected => selected.path === item.path)
            if (index !== -1) {
                this.selectedImages.splice(index, 1)
                return
            }
            if (this.multiple) {
                this.selectedImages.push(item)
            } else {
                this.selectedImages = [item]
            }
        },
        isSelected(item) {
            return this.selectedImages.some(selected => selected.path === item.path)
        },
        selectedIndex(item) {
            return this.selectedImages.findIndex(selected => selected.path === item.path) + 1
        },
        changePage(pageNo) {
            this.loadImage(pageNo)
        },
        showSizeChange(pageSize) {
            this.ipagination.pageSize = pageSize
            this.loadImage(1)
        },
        confirm() {
            if (!this.selectedImages.length) {
                this.$message.warning('请选择图片')
                return
            }
            const result = this.selectedImages.map(item => ({
                id: item.id,
                path: item.path,
                imageName: item.imageName,
                width: item.width,
                height: item.height
            }))
            this.$emit('select', this.multiple ? result : result[0])
            this.close()
        },
        close() {
            this.$emit('update:visible', false)
        }
    }
}
</script>

<style scoped>
.picker-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.dir-select {
    width: 180px;
}

.dir-create {
    width: 240px;
}

.image-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    min-height: 360px;
    max-height: 520px;
    overflow-y: auto;
}

.image-card {
    border: 1px solid #edf0f5;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    overflow: hidden;
}

.image-card.active {
    border-color: #d64545;
    box-shadow: 0 0 0 2px rgba(214, 69, 69, 0.12);
}

.image-card img {
    display: block;
    width: 100%;
    height: 128px;
    object-fit: cover;
    background: #f5f6f8;
}

.image-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 36px;
    padding: 0 8px;
    color: #4b5563;
    font-size: 12px;
}

.image-meta span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.picker-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.selected-count {
    color: #6b7280;
}
</style>
